// UW (Underwriting) Journey Zustand Store
// Manages all state for the UW lending customer journey

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  UWStoreState,
  UWApplication,
  UWPersonalDetails,
  UWAddress,
  UWOccupation,
  UWSourceOfFund,
  UWVehicles,
  UWHousehold,
  UWIncomeContributors,
  UWVulnerabilityState,
  UWDirectDebit,
  UWConsent,
  UWConsentType,
  UWDocumentType,
  UWDecisionStatus,
  UWFieldSource,
} from '@/shared/types/uw';

interface UWStoreActions {
  // Application
  setApplication: (app: UWApplication) => void;
  setApplicationId: (id: string) => void;
  setCustomerId: (id: string) => void;

  // Journey progress
  setCurrentStep: (step: number) => void;
  markStepCompleted: (step: number) => void;

  // KYC data setters
  setPersonalDetails: (details: UWPersonalDetails) => void;
  setDateOfBirth: (dob: string) => void;
  setContact: (contact: { mobile: string; email: string }) => void;
  setAddressHistory: (addresses: UWAddress[]) => void;
  setOccupation: (occupation: UWOccupation) => void;
  setSourceOfFund: (source: UWSourceOfFund) => void;
  setVehicles: (vehicles: UWVehicles) => void;
  setHousehold: (household: UWHousehold) => void;
  setIncomeContributors: (contributors: UWIncomeContributors) => void;
  setVulnerability: (data: Partial<UWVulnerabilityState>) => void;

  // KYC verification
  setIdDocumentType: (type: UWDocumentType | null) => void;
  setIdDocumentUploaded: (uploaded: boolean) => void;
  setSelfieVerified: (verified: boolean) => void;

  // Onfido KYC
  setOnfidoSdkToken: (token: string | null) => void;
  setOnfidoWorkflowRunId: (id: string | null) => void;

  // Financial / Open Banking
  setObConnected: (connected: boolean) => void;
  setObSearchId: (id: string | null) => void;
  setObBankName: (name: string | null) => void;
  setObImportComplete: (complete: boolean) => void;
  setIeCompleted: (completed: boolean) => void;
  setFieldSources: (sources: UWFieldSource[]) => void;

  // Documents
  addUploadedDocument: (doc: { category: string; fileName: string; uploadDate: string }) => void;
  removeUploadedDocument: (fileName: string) => void;

  // Direct Debit
  setDirectDebit: (dd: UWDirectDebit) => void;

  // Consents
  setConsent: (type: UWConsentType, consent: UWConsent) => void;

  // Submission
  setSubmissionStatus: (status: 'idle' | 'submitting' | 'submitted' | 'error') => void;
  setSubmissionRef: (ref: string) => void;
  setDecisionStatus: (status: UWDecisionStatus) => void;

  // Reset
  resetStore: () => void;
}

const initialState: UWStoreState = {
  application: null,
  applicationId: null,
  customerId: null,
  currentStep: 0,
  completedSteps: [],
  personalDetails: null,
  dateOfBirth: null,
  contact: null,
  addressHistory: [],
  occupation: null,
  sourceOfFund: null,
  vehicles: { hasVehicles: null, count: 0 },
  household: { hasOthers: null, childrenUnder16: 0, children16to18: 0, adultsOver18: 0, total: 1 },
  incomeContributors: { hasContributors: null, count: 0 },
  vulnerability: {
    supplementaryPrivacyConsent: '',
    healthConditions: '', communicationAdjustments: '', preferredCommunication: [],
    lifeEvents: [], lifeEventsOther: '',
    unexpectedExpenseResponse: '', incomeStability: '',
    creditConfidence: '', languageSupport: '',
  },
  idDocumentType: null,
  idDocumentUploaded: false,
  selfieVerified: false,
  onfidoSdkToken: null,
  onfidoWorkflowRunId: null,
  obConnected: false,
  obSearchId: null,
  obBankName: null,
  obImportComplete: false,
  ieCompleted: false,
  fieldSources: [],
  uploadedDocuments: [],
  directDebit: null,
  consents: {
    CRA_CHECK: { accepted: false, timestamp: null },
    OPEN_BANKING: { accepted: false, timestamp: null },
    TERMS_CONDITIONS: { accepted: false, timestamp: null },
    DECLARATION_ACCURACY: { accepted: false, timestamp: null },
    DIRECT_DEBIT_AUTH: { accepted: false, timestamp: null },
  },
  submissionStatus: 'idle',
  submissionRef: null,
  decisionStatus: null,
};

export const useUWStore = create<UWStoreState & UWStoreActions>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setApplication: (app) => set({ application: app }),
        setApplicationId: (id) => set({ applicationId: id }),
        setCustomerId: (id) => set({ customerId: id }),

        setCurrentStep: (step) => set({ currentStep: step }),
        markStepCompleted: (step) =>
          set((state) => ({
            completedSteps: state.completedSteps.includes(step)
              ? state.completedSteps
              : [...state.completedSteps, step],
          })),

        setPersonalDetails: (details) => set({ personalDetails: details }),
        setDateOfBirth: (dob) => set({ dateOfBirth: dob }),
        setContact: (contact) => set({ contact }),
        setAddressHistory: (addresses) => set({ addressHistory: addresses }),
        setOccupation: (occupation) => set({ occupation }),
        setSourceOfFund: (source) => set({ sourceOfFund: source }),
        setVehicles: (vehicles) => set({ vehicles }),
        setHousehold: (household) => set({ household }),
        setIncomeContributors: (contributors) => set({ incomeContributors: contributors }),
        setVulnerability: (data) => set((state) => ({ vulnerability: { ...state.vulnerability, ...data } })),

        setIdDocumentType: (type) => set({ idDocumentType: type }),
        setIdDocumentUploaded: (uploaded) => set({ idDocumentUploaded: uploaded }),
        setSelfieVerified: (verified) => set({ selfieVerified: verified }),

        setOnfidoSdkToken: (token) => set({ onfidoSdkToken: token }),
        setOnfidoWorkflowRunId: (id) => set({ onfidoWorkflowRunId: id }),

        setObConnected: (connected) => set({ obConnected: connected }),
        setObSearchId: (id) => set({ obSearchId: id }),
        setObBankName: (name) => set({ obBankName: name }),
        setObImportComplete: (complete) => set({ obImportComplete: complete }),
        setIeCompleted: (completed) => set({ ieCompleted: completed }),
        setFieldSources: (sources) => set({ fieldSources: sources }),

        addUploadedDocument: (doc) =>
          set((state) => ({
            uploadedDocuments: [...state.uploadedDocuments, doc],
          })),
        removeUploadedDocument: (fileName) =>
          set((state) => ({
            uploadedDocuments: state.uploadedDocuments.filter((d) => d.fileName !== fileName),
          })),

        setDirectDebit: (dd) => set({ directDebit: dd }),

        setConsent: (type, consent) =>
          set((state) => ({
            consents: { ...state.consents, [type]: consent },
          })),

        setSubmissionStatus: (status) => set({ submissionStatus: status }),
        setSubmissionRef: (ref) => set({ submissionRef: ref }),
        setDecisionStatus: (status) => set({ decisionStatus: status }),

        resetStore: () => set(initialState),
      }),
      {
        name: 'uw-journey-storage',
        version: 7,
        migrate: (persistedState, oldVersion) => {
          const p = { ...(persistedState ?? {}) } as Record<string, unknown>;
          let completedSteps = (p.completedSteps as number[]) ?? [];

          // v1 default: hasVehicles false + count 0; keep real "No" if Vehicles step completed (index 6).
          if (oldVersion < 2) {
            const vehicles = p.vehicles as { hasVehicles?: boolean | null; count?: number } | undefined;
            if (
              vehicles &&
              vehicles.hasVehicles === false &&
              (vehicles.count ?? 0) === 0 &&
              !completedSteps.includes(6)
            ) {
              p.vehicles = { hasVehicles: null, count: 0 };
            }
          }

          // v2 default: hasOthers false + zero counts; keep real "No" if Household step completed (index 7).
          if (oldVersion < 3) {
            const household = p.household as {
              hasOthers?: boolean | null;
              childrenUnder16?: number;
              children16to18?: number;
              adultsOver18?: number;
              total?: number;
            } | undefined;
            if (
              household &&
              household.hasOthers === false &&
              (household.childrenUnder16 ?? 0) === 0 &&
              (household.children16to18 ?? 0) === 0 &&
              (household.adultsOver18 ?? 0) === 0 &&
              !completedSteps.includes(7)
            ) {
              p.household = { ...household, hasOthers: null };
            }
          }

          // v4 default: hasContributors false + count 0; keep real "No" if Income contributors step completed (index 8).
          if (oldVersion < 4) {
            const ic = p.incomeContributors as {
              hasContributors?: boolean | null;
              count?: number;
            } | undefined;
            if (
              ic &&
              ic.hasContributors === false &&
              (ic.count ?? 0) === 0 &&
              !completedSteps.includes(8)
            ) {
              p.incomeContributors = { ...ic, hasContributors: null };
            }
          }

          // v5: Income Contributors → step 6 (after Source of Fund); Vehicles → 7; Household → 8
          if (oldVersion < 5) {
            const mapUwOnboardingStepIndex = (s: number): number => {
              if (s === 6) return 7;
              if (s === 7) return 8;
              if (s === 8) return 6;
              return s;
            };
            completedSteps = [...new Set(completedSteps.map(mapUwOnboardingStepIndex))].sort((a, b) => a - b);
            p.completedSteps = completedSteps;
            const cur = p.currentStep;
            if (typeof cur === 'number' && Number.isFinite(cur)) {
              p.currentStep = mapUwOnboardingStepIndex(cur);
            }
          }

          // v6: Occupation role date fields renamed and semantics changed:
          // - from (currentRoleYears/currentRoleMonths) tenure → (currentRoleStartYear/currentRoleStartMonth) start date.
          if (oldVersion < 6) {
            const occ = p.occupation as
              | {
                  primary?: Record<string, unknown>;
                  secondary?: Record<string, unknown>;
                }
              | null
              | undefined;

            const migrateEntry = (entry: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
              if (!entry) return entry;
              const years = entry.currentRoleYears;
              const months = entry.currentRoleMonths;

              const yearNum = typeof years === 'number' && Number.isFinite(years) ? years : null;
              const monthNum = typeof months === 'number' && Number.isFinite(months) ? months : null;

              // If it looks like legacy tenure (small years), clear so user re-enters.
              if (yearNum !== null && yearNum < 100) {
                const { currentRoleYears: _cy, currentRoleMonths: _cm, ...rest } = entry;
                return { ...rest, currentRoleStartYear: null, currentRoleStartMonth: null };
              }

              // If it looks like already-a-start-year (>= 1000), rename.
              if (yearNum !== null && yearNum >= 1000) {
                const { currentRoleYears: _cy, currentRoleMonths: _cm, ...rest } = entry;
                return { ...rest, currentRoleStartYear: yearNum, currentRoleStartMonth: monthNum };
              }

              // If already on the new keys, leave it as-is.
              if (typeof entry.currentRoleStartYear === 'number' || entry.currentRoleStartYear === null) return entry;

              return entry;
            };

            if (occ && typeof occ === 'object') {
              const primary = migrateEntry(occ.primary);
              const secondary = migrateEntry(occ.secondary);
              p.occupation = { ...occ, ...(primary ? { primary } : {}), ...(secondary ? { secondary } : {}) };
            }
          }

          // v7: Add Onfido KYC fields (null defaults — no data migration needed)
          if (oldVersion < 7) {
            if (p.onfidoSdkToken === undefined) p.onfidoSdkToken = null;
            if (p.onfidoWorkflowRunId === undefined) p.onfidoWorkflowRunId = null;
          }

          return p;
        },
        partialize: (state) => ({
          // Persist journey progress and KYC data so user can resume on re-login
          applicationId: state.applicationId,
          customerId: state.customerId,
          currentStep: state.currentStep,
          completedSteps: state.completedSteps,
          personalDetails: state.personalDetails,
          dateOfBirth: state.dateOfBirth,
          contact: state.contact,
          addressHistory: state.addressHistory,
          occupation: state.occupation,
          sourceOfFund: state.sourceOfFund,
          vehicles: state.vehicles,
          household: state.household,
          incomeContributors: state.incomeContributors,
          vulnerability: state.vulnerability,
          idDocumentType: state.idDocumentType,
          idDocumentUploaded: state.idDocumentUploaded,
          selfieVerified: state.selfieVerified,
          onfidoSdkToken: state.onfidoSdkToken,
          onfidoWorkflowRunId: state.onfidoWorkflowRunId,
          obConnected: state.obConnected,
          obSearchId: state.obSearchId,
          obBankName: state.obBankName,
          obImportComplete: state.obImportComplete,
          ieCompleted: state.ieCompleted,
          uploadedDocuments: state.uploadedDocuments,
          directDebit: state.directDebit,
          consents: state.consents,
          submissionStatus: state.submissionStatus,
          submissionRef: state.submissionRef,
          decisionStatus: state.decisionStatus,
        }),
      }
    ),
    { name: 'uw-store' }
  )
);
